import { TableFooterComponent } from './table-footer/table-footer.component';
import { TableBodyComponent } from './table-body/table-body.component';
import { TableHeaderComponent } from './table-header/table-header.component';
import { TableColumnComponent } from './components/column/table-column.component';
import {
    Component, ContentChild,
    ContentChildren,
    EventEmitter,
    Input,
    Output,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { TableConfig } from '@app/shared/util/table-config.utils';
import { MenuItem } from "primeng/api";
import { ContextMenu } from "primeng/contextmenu";
import { Log } from "ng2-logger";
import { TranslateService } from "@ngx-translate/core";
import { Router } from '@angular/router';
import { TableInfoRowComponent } from "@components/custom-table/components/info-row/info-row.component";

@Component({
    selector: 'app-table',
    templateUrl: 'table.component.html',
    styleUrls: ['table.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class TableComponent {
    _logger = Log.create(TableComponent.name);

    @ContentChildren(TableColumnComponent) columns: QueryList<TableColumnComponent>;
    @ContentChild(TableInfoRowComponent) infoRow: TableInfoRowComponent;
    @ViewChild(ContextMenu, {static: false}) contextMenu: ContextMenu;
    @ViewChild(TableHeaderComponent, {static: false}) header: TableHeaderComponent;
    @ViewChild(TableBodyComponent, {static: false}) body: TableBodyComponent;
    @ViewChild(TableFooterComponent, {static: false}) footer: TableFooterComponent;

    @Input() data = [];
    @Input() groupableColumns = [];
    @Input() isLoading = false;
    @Input() title;
    @Input() hasTitle = true;
    @Input() hasFooter = true;
    @Input() rowExpand = false;
    @Input() treeMode = false;
    @Input() selectableRows = false;
    @Input() filtersToggle = true;
    @Input() showFilters = true;
    @Input() displayToolsUsageLegend = false;
    @Input() config = new TableConfig();
    @Input() showGroupFilters = false;
    @Input() groupFilters = [];
    @Input() showGroupers = false;
    @Input() directlyGrouped = false;
    @Input() hasPagination = true;
    @Input() rowDuplicate = false;
    @Input() rowNewPage = false;
    @Input() contextMenuItemsFactory: (event: any) => MenuItem[];
    @Input() headerBackgroundColor: string;
    @Input() groupedTools;
    @Input() displayToggleFilters = true;
    @Input() saveFilters = false;
    @Input() filterBySecLinesId: number;
    @Input() isFilterExpanded = false;
    @Input() isMaterial = true;
    @Input() childTemplate: TemplateRef<any>;
    @Input() definedGroupers: boolean;
    @Input() allExpanded = false;
    @Input() notMyProblem = false;
    @Input() hasSelectAll = false;
    @Input() isSelectAllChecked = false;

    @Output() sortChange = new EventEmitter<any>();
    @Output() filterChange = new EventEmitter<any>();
    @Output() filterGroupChange = new EventEmitter<any>();
    @Output() groupChange = new EventEmitter<any>();
    @Output() pageChange = new EventEmitter<any>();
    @Output() rowClick = new EventEmitter<any>();
    @Output() filterToggle = new EventEmitter<any>();
    @Output() duplicateClick = new EventEmitter<any>();
    @Output() openInNewPage = new EventEmitter<any>();
    @Output() rowExpandClick = new EventEmitter<any>();
    @Output() selectAll = new EventEmitter<any>();
    @Output() unSelectAll = new EventEmitter<any>();

    selectedRecord: any;
    contextMenuItems: MenuItem[];


    includeSecondaryLines = false;
    filteredByLine: any;

    parentComponent = '';

    constructor(private translateService: TranslateService, private router: Router) {
        this.parentComponent = this.router.url;
    }

    onSortChange(config) {
        this.sortChange.emit(config);
    }

    onFilterChange(config) {
        if (this.hasPagination) {
            this.resetPagination();
        }
        this.filterChange.emit(config);
    }

    onFilterToggled(showFilters: boolean) {
        this.showFilters = showFilters;
    }

    onFilterGroupChanged(config) {
        if (this.hasPagination) {
            this.resetPagination();
        }
        this.filterGroupChange.emit(config);
    }

    onGroupChange(config) {
        this.groupChange.emit(config);
    }

    onRowClick(item) {
        this.rowClick.emit(item);
    }

    onRightRowClick(event) {
        this.selectedRecord = event.record;
        this.contextMenuItems = this.contextMenuItemsFactory
            ? this.contextMenuItemsFactory(event)
            : this.buildContextMenuItems(event);
        if (this.contextMenuItems?.length) {
            this._logger.info('onRightRowClick::', event);
            this.contextMenu.show(event.event);
        }
    }

    onDuplicate(event) {
        this._logger.info('onDuplicate::', event);
        this.duplicateClick.emit(Object.assign({}, event));
    }

    onOpenInNewPage(event) {
        this._logger.info('onOpenInNewPage::', event);
        this.openInNewPage.emit(event);
    }

    onPageChange(pagination) {
        this._logger.info('onPageChange::', pagination);
        this.pageChange.emit(this.config);
    }

    onExpandAll(expanded) {
        this.allExpanded = expanded;
        this.body.toggleExpandAll(expanded);
    }

    onFilterToggle(event: any) {
        if (event && event.type === 'first-filter') {
            this.includeSecondaryLines = event.event;
        }

        this.filterToggle.emit(event);
    }

    onSelectAll() {
        this.selectAll.emit();
    }

    onUnselectAll() {
        this.unSelectAll.emit();
    }

    onFilteredByLine(event: any) {
        this.filteredByLine = event;
    }

    private buildContextMenuItems(event) {
        if (event.record['isDeleted'] === true) {
            return [
                {label: this.translateService.instant('NotAvailable')}
            ]
        }

        const actions = [];
        if (this.rowDuplicate) {
            actions.push({
                label: this.translateService.instant('generic.buttons.duplicate'),
                icon: 'fa fa-copy',
                command: () => {
                    this.onDuplicate(this.selectedRecord);
                }
            });
        }
        if (this.rowNewPage) {
            actions.push({
                label: this.translateService.instant('generic.buttons.openNewTab'),
                icon: 'fa fa-external-link',
                command: () => {
                    this.onOpenInNewPage(this.selectedRecord);
                }
            });
        }

        return actions;
    }

    private resetPagination() {
        const paginator = this.footer.paginator.paginator;
        const storedPaginationDetails = JSON.parse(sessionStorage.getItem(this.parentComponent + '.table.paginationDetails'));
        if (paginator && !paginator.isFirstPage() && !storedPaginationDetails) {
            const firstPage = 0; // The 0 magic variable represents the 1st page
            paginator.changePage(firstPage);
            return;
        }
    }

    onRowExpandClick(record: any) {
        this.body.toggleRowExpand(record);
    }
}
